import { ThemeEvents } from '@theme/events';

const SIZE_OPTION_NAMES = ['Size | ET', 'Size', 'Size/ET'];
const BOLT_OPTION_NAMES = ['Bolt Pattern', 'PCD', 'Bolt'];

/** @param {string} text */
function normalizeText(text) {
  return text.trim().replace(/\s*-\s*Unavailable\s*$/i, '');
}

/** @param {string} text */
function normalizeKey(text) {
  return normalizeText(text).replace(/×/g, 'x').replace(/丨/g, '|').toLowerCase();
}

function getMainVariantPicker() {
  const bundlesRoot = document.querySelector('[data-testid="product-bundles"]');
  const productInfo =
    bundlesRoot?.closest('.product-information') || document.querySelector('.product-information');

  const picker =
    productInfo?.querySelector('variant-picker[data-template-product-match="true"]') ||
    productInfo?.querySelector('variant-picker') ||
    document.querySelector('variant-picker[data-template-product-match="true"]');

  return picker instanceof HTMLElement ? picker : null;
}

/**
 * @param {HTMLSelectElement} select
 * @returns {string}
 */
function getSelectOptionValue(select) {
  const option = select.selectedOptions[0];
  if (!option) return '';

  return normalizeText(option.value || option.textContent || '');
}

/**
 * @param {HTMLElement} picker
 * @param {string[]} names
 * @returns {string}
 */
function getNamedSelectValue(picker, names) {
  for (const select of picker.querySelectorAll('select.variant-option__select')) {
    if (!(select instanceof HTMLSelectElement)) continue;
    if (select.hasAttribute('data-finishes-dialog-option')) continue;
    if (select.closest('[hidden]')) continue;

    const label = select.name.replace(/^options\[(.+)\]$/, '$1').trim();
    const matches = names.some((name) => label.toLowerCase() === name.toLowerCase());
    if (!matches) continue;

    return getSelectOptionValue(select);
  }

  for (const fieldset of picker.querySelectorAll('fieldset')) {
    const legend = fieldset.querySelector('legend');
    const label = legend?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
    const matches = names.some((name) => label.toLowerCase() === name.toLowerCase());
    if (!matches) continue;

    const checked = fieldset.querySelector('input:checked');
    if (!(checked instanceof HTMLInputElement)) continue;

    return normalizeText(checked.value || checked.getAttribute('aria-label') || '');
  }

  return '';
}

/**
 * @param {HTMLElement} picker
 * @returns {string[]}
 */
function getVisibleDropdownValues(picker) {
  /** @type {string[]} */
  const values = [];

  for (const select of picker.querySelectorAll('select.variant-option__select')) {
    if (!(select instanceof HTMLSelectElement)) continue;
    if (select.hasAttribute('data-finishes-dialog-option')) continue;
    if (select.closest('.variant-option--finishes-dialog')) continue;
    if (select.closest('[hidden]')) continue;

    const value = getSelectOptionValue(select);
    if (value) values.push(value);
  }

  return values;
}

/**
 * @param {HTMLElement} picker
 * @returns {string}
 */
function getFinishValue(picker) {
  const finishLabel = picker.querySelector('[data-finish-selected-label]');
  if (finishLabel instanceof HTMLElement && !finishLabel.hidden) {
    const finish = normalizeText(finishLabel.textContent ?? '');
    if (finish) return finish;
  }

  const checkedFinish = picker.querySelector('[data-finish-item][data-current-checked="true"]');
  if (checkedFinish instanceof HTMLElement) {
    const finish = normalizeText(checkedFinish.dataset.finishName ?? '');
    if (finish) return finish;
  }

  const hiddenFinish = picker.querySelector('select[data-finishes-dialog-option]');
  if (hiddenFinish instanceof HTMLSelectElement) {
    return getSelectOptionValue(hiddenFinish);
  }

  return '';
}

/**
 * @param {HTMLElement} picker
 * @returns {{ finish: string, size: string, bolt: string } | null}
 */
function getMainProductSpec(picker) {
  const finish = getFinishValue(picker);
  let size = getNamedSelectValue(picker, SIZE_OPTION_NAMES);
  let bolt = getNamedSelectValue(picker, BOLT_OPTION_NAMES);

  if (!size || !bolt) {
    const dropdownValues = getVisibleDropdownValues(picker);
    if (!size && dropdownValues[0]) size = dropdownValues[0];
    if (!bolt && dropdownValues[1]) bolt = dropdownValues[1];
  }

  if (!finish && !size && !bolt) return null;

  return { finish, size, bolt };
}

/**
 * @param {HTMLOptionElement} option
 * @returns {{ finish: string, size: string, bolt: string } | null}
 */
function getBundleSpecFromOption(option) {
  const finish = option.dataset.bundleFinish || '';
  const size = option.dataset.bundleSize || '';
  const bolt = option.dataset.bundleBolt || '';

  if (finish || size || bolt) {
    return { finish, size, bolt };
  }

  const parts = normalizeText(option.textContent ?? '').split(/\s*\/\s*/);
  if (parts.length < 2) return null;

  if (parts.length === 2) {
    return { finish: parts[0], size: parts[1], bolt: '' };
  }

  return {
    finish: parts.slice(0, -2).join(' / '),
    size: parts[parts.length - 2],
    bolt: parts[parts.length - 1],
  };
}

/**
 * @param {{ finish: string, size: string, bolt: string }} mainSpec
 * @param {{ finish: string, size: string, bolt: string }} bundleSpec
 * @returns {boolean}
 */
function specsMatch(mainSpec, bundleSpec) {
  if (bundleSpec.bolt) {
    if (!mainSpec.bolt || normalizeKey(mainSpec.bolt) !== normalizeKey(bundleSpec.bolt)) {
      return false;
    }
  }

  if (bundleSpec.size) {
    if (!mainSpec.size || normalizeKey(mainSpec.size) !== normalizeKey(bundleSpec.size)) {
      return false;
    }
  }

  if (bundleSpec.finish) {
    if (!mainSpec.finish || normalizeKey(mainSpec.finish) !== normalizeKey(bundleSpec.finish)) {
      return false;
    }
  }

  return true;
}

/**
 * @param {HTMLSelectElement} select
 * @param {{ finish: string, size: string, bolt: string }} mainSpec
 * @returns {HTMLOptionElement | null}
 */
function findMatchingBundleOption(select, mainSpec) {
  /** @type {HTMLOptionElement | null} */
  let disabledMatch = null;

  for (const option of select.options) {
    const bundleSpec = getBundleSpecFromOption(option);
    if (!bundleSpec || !specsMatch(mainSpec, bundleSpec)) continue;

    if (!option.disabled) return option;
    if (!disabledMatch) disabledMatch = option;
  }

  return disabledMatch;
}

/**
 * @param {HTMLSelectElement} select
 * @param {HTMLOptionElement} option
 */
function applyBundleSelection(select, option) {
  if (select.value === option.value) {
    updateBundleAddButton(select, option);
    return;
  }

  select.value = option.value;

  const variantInput = select.closest('form')?.querySelector('input[name="id"]');
  if (variantInput instanceof HTMLInputElement) {
    variantInput.value = option.value;
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateBundleAddButton(select, option);
}

/**
 * @param {HTMLSelectElement} select
 * @param {HTMLOptionElement} option
 */
function updateBundleAddButton(select, option) {
  const addButton = select.closest('product-form-component')?.querySelector('button[type="submit"]');
  if (addButton instanceof HTMLButtonElement) {
    addButton.disabled = option.disabled;
  }
}

/**
 * @param {HTMLElement} item
 * @param {boolean} hidden
 */
function setBundleItemHidden(item, hidden) {
  item.classList.toggle('is-bundle-hidden', hidden);
  item.toggleAttribute('hidden', hidden);
}

function updateBundlesSectionVisibility() {
  const section = document.querySelector('[data-testid="product-bundles"]');
  if (!(section instanceof HTMLElement)) return;

  const hasVisibleItem = section.querySelector('.product-bundles__item:not(.is-bundle-hidden)');
  section.classList.toggle('is-bundles-hidden', !hasVisibleItem);
  section.toggleAttribute('hidden', !hasVisibleItem);
}

function syncBundleVariantSelects() {
  const picker = getMainVariantPicker();
  if (!picker) return;

  const mainSpec = getMainProductSpec(picker);
  if (!mainSpec) return;

  document.querySelectorAll('[data-bundle-variant-select]').forEach((select) => {
    if (!(select instanceof HTMLSelectElement)) return;

    const item = select.closest('.product-bundles__item');
    const match = findMatchingBundleOption(select, mainSpec);

    if (item instanceof HTMLElement) {
      setBundleItemHidden(item, !match);
    }

    if (match) applyBundleSelection(select, match);
  });

  updateBundlesSectionVisibility();
}

function scheduleSync() {
  requestAnimationFrame(() => {
    requestAnimationFrame(syncBundleVariantSelects);
  });
}

document.addEventListener(
  'change',
  (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (event.target.matches('[data-bundle-variant-select]')) return;
    if (!event.target.closest('.product-information variant-picker')) return;

    scheduleSync();
  },
  true
);

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLSelectElement)) return;
  if (!target.matches('[data-bundle-variant-select]')) return;

  const option = target.selectedOptions[0];
  if (!option) return;

  const variantInput = target.closest('form')?.querySelector('input[name="id"]');
  if (variantInput instanceof HTMLInputElement) {
    variantInput.value = target.value;
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateBundleAddButton(target, option);
});

document.addEventListener(ThemeEvents.finishSelected, scheduleSync);
document.addEventListener(ThemeEvents.variantUpdate, scheduleSync);
document.addEventListener(ThemeEvents.variantSelected, scheduleSync);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleSync);
} else {
  scheduleSync();
}
